(ns frontend.view.entity-viewers.scope
  "Keeps a live entity-scope subscription open while a view is mounted.

   A new-grid account does not get business records from the bulk sync or from
   the live-update channel (ai/sync-model.md). A view that reads such records
   from `@session/entities` owns an entity-scope subscription. The root of the
   subscription is the entity that the view shows. This component does the
   open, re-open and close steps, so that a view does not repeat them."
  (:require
   [frontend.logging :as logging]
   [frontend.updates.entity-scope :as entity-scope]
   [reagent.core :as r]))

(defn host
  "Renders `children`. It keeps `entity-scope/open-merging!` running for
   `:entity-id` and closes it on unmount. It opens the subscription again when
   `:entity-id` or `:instance-id` changes.

   Props:
   - `:entity-id`    root entity of the scope. The component opens nothing
                     while this value is not an int.
   - `:instance-id`  keyword that is unique for each view. Two views can then
                     hold a subscription on the same root.
   - `:enabled?`     default true. When it is false, the component renders only
                     its children. Pass `(session/new-grid-2023?)` when an
                     old-grid account already has the data from the bulk sync.
                     Its behavior then does not change.
   - `:loading-view` optional hiccup. The component shows it instead of the
                     children until the first snapshot or an error arrives. Use
                     it for a view that reads its root from the session. The view
                     then does not show \"not found\" for a short time.
   - `:on-entities`  optional. The component calls it with the entities that
                     each message merges, including the first snapshot.
   - `:on-message-summary`
                     optional. The component calls it with
                     `entity-scope/message-summary` of each message: the
                     upserts, the ids that left the scope and whether the
                     message is a snapshot. Use it when the view keeps its own
                     copy of the scope, so that it can replace the copy after a
                     reconnect.
   - `:on-error`     optional. The component calls it with the error. The
                     default logs the error."
  [_props & _children]
  (let [subscribed* (atom nil)
        loaded?*    (r/atom false)
        close!      (fn []
                      (when-let [{:keys [entity-id instance-id]} @subscribed*]
                        (entity-scope/close! entity-id instance-id)
                        (reset! subscribed* nil)))
        sync!       (fn [{:keys [entity-id instance-id enabled? on-entities on-message-summary on-error]
                          :or   {enabled? true}}]
                      (let [target {:entity-id   entity-id
                                    :instance-id instance-id}]
                        (when-not (= target @subscribed*)
                          (close!)
                          ;; With no scope, there is nothing to wait for.
                          (when-not (and enabled?
                                         (int? entity-id))
                            (reset! loaded?* true))
                          (when (and enabled?
                                     (int? entity-id))
                            (reset! subscribed* target)
                            (reset! loaded?* false)
                            (entity-scope/open-merging!
                             {:entity-id          entity-id
                              :instance-id        instance-id
                              :on-message-summary on-message-summary
                              :on-entities        (fn [entities]
                                                    (reset! loaded?* true)
                                                    (when (fn? on-entities)
                                                      (on-entities entities)))
                              :on-error           (fn [error]
                                                    (reset! loaded?* true)
                                                    (if (fn? on-error)
                                                      (on-error error)
                                                      (logging/error
                                                       "Entity scope subscription failed : "
                                                       instance-id " " entity-id " >> " error)))})))))]
    (r/create-class
     {:component-did-mount
      (fn [this]
        (sync! (r/props this)))

      :component-did-update
      (fn [this _old-argv]
        (sync! (r/props this)))

      :component-will-unmount
      (fn [_this]
        (close!))

      :reagent-render
      (fn [{:keys [enabled? loading-view] :or {enabled? true}} & children]
        (if (and enabled?
                 loading-view
                 (not @loaded?*))
          loading-view
          (into [:<>] children)))})))
